import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Car, ChevronRight, Gauge, RotateCcw, ShieldCheck, Sprout, Truck } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { API, PORTAL_URL, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";
import { ESTIMATE_NOTE, TUNING_CATEGORIES } from "@/lib/tuningCategories";
import { EstimatedBadge } from "@/pages/TuningCategoryIndex";

const inputCls = "h-12 w-full border border-line bg-white px-3 text-sm text-ink outline-none focus:border-regal-amber transition-colors disabled:cursor-not-allowed disabled:opacity-40";
const labelCls = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500";

const STEPS = [
  { key: "vehicle_type", label: "Vehicle Type", testId: "td-step-type" },
  { key: "make", label: "Manufacturer", testId: "td-step-make" },
  { key: "model", label: "Model", testId: "td-step-model" },
  { key: "generation", label: "Generation / Year", testId: "td-step-generation" },
  { key: "engine", label: "Engine / Variant", testId: "td-step-engine" },
];

const FAQS = [
  ["What is a Stage 1 remap?", "A Stage 1 remap is a software-only ECU recalibration for a completely standard vehicle — no hardware modifications are required. Fuelling, boost pressure and torque limiters are optimised within safe tolerances for the engine and drivetrain."],
  ["Where do the figures in this database come from?", "Original figures are manufacturer-published factory specifications. Stage 1 figures for cars, vans and trucks come only from verified Regal Tuning calibration data, developed on our in-house 4WD dynamometer and proven across our dealer network. Agricultural machinery also includes clearly-labelled Estimated Stage 1 figures — conservative, engine- and ECU-based calculations shown only where verified data is unavailable."],
  ["Are the gains guaranteed for my vehicle?", "Gains depend on the exact vehicle, engine health, fuel quality and conditions. Where Stage 1 figures are published they reflect verified calibrations; for any vehicle we confirm expected gains in writing before work begins."],
  ["My vehicle isn't listed — can you still remap it?", "Almost certainly. This database is being expanded continuously and currently shows a fraction of our coverage. Request a quote with your registration or vehicle details and our calibration team will confirm support and expected gains."],
];

function StepSelect({ index, label, testId, value, onChange, options, disabled }) {
  return (
    <div>
      <label className={labelCls} htmlFor={testId}>
        <span className={`mr-1.5 inline-grid h-4 w-4 place-items-center rounded-full font-mono text-[9px] ${value ? "bg-regal-amber text-white" : "bg-line text-neutral-500"}`}>{index + 1}</span>
        {label}
      </label>
      <select id={testId} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className={inputCls}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function PerfRow({ label, unit, original, tuned, gain, extraStages, testId, s1Estimated, originalEstimated }) {
  const pct = gain != null && original ? Math.round((gain / original) * 100) : null;
  return (
    <tr className="border-b border-line last:border-0" data-testid={testId}>
      <td className="px-4 py-4 sm:px-6">
        <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{label}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{unit}</p>
      </td>
      <td className="px-4 py-4 text-center sm:px-6" data-testid={`${testId}-original`}>
        <p className="font-display text-2xl sm:text-3xl font-extrabold text-neutral-400">
          {original ?? "—"}
          {originalEstimated && original != null && <EstimatedBadge testId={`${testId}-original-est`} />}
        </p>
      </td>
      <td className="bg-regal-amber/5 px-4 py-4 text-center sm:px-6" data-testid={`${testId}-stage1`}>
        <p className="font-display text-2xl sm:text-3xl font-extrabold text-regal-amber">
          {tuned ?? "—"}
          {s1Estimated && tuned != null && <EstimatedBadge testId={`${testId}-stage1-est`} />}
        </p>
      </td>
      <td className="px-4 py-4 text-center sm:px-6" data-testid={`${testId}-gain`}>
        {gain != null ? (
          <>
            <p className="font-display text-xl sm:text-2xl font-extrabold text-emerald-700">+{gain}</p>
            {pct != null && <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-700">+{pct}%</p>}
          </>
        ) : (
          <p className="font-display text-2xl font-extrabold text-neutral-300">—</p>
        )}
      </td>
      {(extraStages || []).map((s) => {
        const v = unit === "BHP" ? s.bhp : s.nm;
        const g = v != null && original != null ? v - original : null;
        const p = g != null && original ? Math.round((g / original) * 100) : null;
        return (
          <td key={s.key} className="bg-regal-amber/5 px-4 py-4 text-center sm:px-6" data-testid={`${testId}-${s.key}`}>
            <p className="font-display text-2xl sm:text-3xl font-extrabold text-regal-amber">
              {v ?? "—"}
              {s.estimated && v != null && <EstimatedBadge testId={`${testId}-${s.key}-est`} />}
            </p>
            {g != null && (
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-700">+{g} {unit}{p != null ? ` · +${p}%` : ""}</p>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function CompareBar({ label, unit, original, tuned, testId }) {
  const max = Math.max(original || 0, tuned || 0) || 1;
  return (
    <div data-testid={testId}>
      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">{label} ({unit})</p>
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <div className="h-3 bg-neutral-300" style={{ width: `${(original / max) * 100}%` }} />
          <span className="font-mono text-[11px] text-neutral-500">{original}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-3 bg-regal-amber" style={{ width: `${(tuned / max) * 100}%` }} />
          <span className="font-mono text-[11px] font-bold text-regal-amber">{tuned}</span>
        </div>
      </div>
    </div>
  );
}

export default function TuningDatabase() {
  const { make: makeSlug, model: modelSlug, engine: engineSlug } = useParams();
  const empty = { vehicle_type: "", make: "", model: "", generation: "", engine: "" };
  const [sel, setSel] = useState(empty);
  const [options, setOptions] = useState(null);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState(null);
  const [miss, setMiss] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setJsonLd("td-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
      ],
    });
    setJsonLd("td-faq", {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([name, text]) => ({ "@type": "Question", name, acceptedAnswer: { "@type": "Answer", text } })),
    });
    return () => { removeJsonLd("td-breadcrumb"); removeJsonLd("td-faq"); };
  }, []);

  // Deep-link support: /tuning-database/audi/a3/2-0-tdi-150 preselects the vehicle.
  useEffect(() => {
    if (makeSlug && modelSlug && engineSlug) {
      axios.get(`${API}/tuning-database/vehicle/${makeSlug}/${modelSlug}/${engineSlug}`)
        .then((r) => {
          const d = r.data;
          setSel({ vehicle_type: d.vehicle_type || "", make: d.make, model: d.model, generation: d.generation || "", engine: d.engine });
        })
        .catch(() => setMiss(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeSlug, modelSlug, engineSlug]);

  // Cascade options — each level depends on the selections above it.
  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(sel).forEach(([k, v]) => v && p.set(k, v));
    axios.get(`${API}/tuning-database/filters?${p}`)
      .then((r) => { setOptions(r.data.options); setTotal(r.data.total); })
      .catch(() => {});
  }, [sel]);

  // Fetch the performance record once the vehicle is fully specified.
  useEffect(() => {
    if (!sel.make || !sel.model || !sel.engine) { setResult(null); return; }
    if (!sel.generation && options && options.generation.length > 0) { setResult(null); return; }
    setBusy(true);
    setMiss(false);
    const p = new URLSearchParams();
    Object.entries(sel).forEach(([k, v]) => v && p.set(k, v));
    axios.get(`${API}/tuning-database/result?${p}`)
      .then((r) => setResult(r.data))
      .catch(() => { setResult(null); setMiss(true); })
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, options]);

  const choose = (i, v) => {
    const next = { ...sel, [STEPS[i].key]: v };
    for (let j = i + 1; j < STEPS.length; j++) next[STEPS[j].key] = "";
    setSel(next);
    setResult(null);
    setMiss(false);
  };

  const reset = () => { setSel(empty); setResult(null); setMiss(false); };

  const years = result ? (result.year_from ? `${result.year_from}${result.year_to ? `–${result.year_to}` : "–present"}` : "") : "";
  const vehicleName = result ? `${result.make} ${result.model} ${result.engine}${years ? ` (${years})` : ""}` : "";
  const quoteUrl = `/contact?subject=${encodeURIComponent(`Remap quote — ${vehicleName}`)}&vehicle=${encodeURIComponent(vehicleName)}`;
  const hasStage1 = result && (result.stage1_bhp != null || result.stage1_nm != null);
  // extra published stages beyond Stage 1 (Celtic-preferred progression, OLSx gap-fills)
  const extraStageKeys = ["stage2", "stage3", "e85", "e85plus", "eco"];
  const extraStages = result ? extraStageKeys.map((k) => (result.display_stages || []).find((s) => s.key === k)).filter(Boolean) : [];
  const isEstimated = result?.data_status === "estimated";
  const ecu = result?.ecus?.find((e) => (e.designation || "").trim());

  const CATEGORY_CARDS = [
    { key: "cars", icon: Car, name: "Cars & Vans", desc: "Hatchbacks to hypercars — 13,000+ vehicles with verified Stage 1 gains.", to: null, testId: "td-cat-cars" },
    { key: "trucks", icon: Truck, name: TUNING_CATEGORIES.trucks.name, desc: "Tractor units, rigids and HGV — verified remapping gains for fleet and haulage.", to: TUNING_CATEGORIES.trucks.basePath, testId: "td-cat-trucks" },
    { key: "agriculture", icon: Sprout, name: TUNING_CATEGORIES.agriculture.name, desc: "Tractors, combines, foragers and self-propelled machinery.", to: TUNING_CATEGORIES.agriculture.basePath, testId: "td-cat-agriculture" },
  ];

  return (
    <main data-testid="tuning-database-page" className="pt-32 sm:pt-40 pb-24">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">Tuning Database</span>
        </nav>

        <p className="overline-label mb-4">Vehicle Tuning Gains</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink">
          Tuning Database
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
          Find your vehicle and compare original factory output with Regal Tuning Stage 1 performance. Cars, vans and trucks show verified calibration figures only; agricultural machinery also publishes conservative Stage 1 estimates — always clearly labelled Estimated — where verified figures are unavailable.
        </p>

        {/* CATEGORY SECTIONS — Cars & Vans / Trucks & HGV / Agriculture stay clearly separated */}
        <div className="mt-10 grid gap-3 sm:grid-cols-3" data-testid="td-categories">
          {CATEGORY_CARDS.map((c) => {
            const Icon = c.icon;
            const inner = (
              <>
                <Icon size={22} className="text-regal-amber" strokeWidth={1.5} />
                <p className="mt-3 font-display text-base font-bold uppercase tracking-wide text-ink group-hover:text-regal-amber transition-colors">{c.name}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-neutral-400">{c.desc}</p>
                <p className="mt-3 inline-flex items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-regal-amber">
                  {c.to ? "Browse section" : "Use the selector below"} <ArrowUpRight size={12} />
                </p>
              </>
            );
            return c.to ? (
              <Link key={c.key} to={c.to} data-testid={c.testId} className="group border border-line bg-white p-6 transition-colors hover:border-regal-amber">
                {inner}
              </Link>
            ) : (
              <button
                key={c.key}
                type="button"
                data-testid={c.testId}
                onClick={() => document.getElementById("td-selector")?.scrollIntoView({ behavior: "smooth" })}
                className="group border border-line bg-white p-6 text-left transition-colors hover:border-regal-amber"
              >
                {inner}
              </button>
            );
          })}
        </div>

        {/* SELECTOR */}
        <div className="mt-10 border border-line bg-surface p-6 sm:p-8" data-testid="td-selector" id="td-selector">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500">
              {total > 0 ? `${total.toLocaleString()} vehicles listed — more added continuously` : "Database is being populated"}
            </p>
            {(sel.vehicle_type || sel.make) && (
              <button onClick={reset} data-testid="td-reset-button" className="inline-flex items-center gap-2 border border-linebright px-4 py-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                <RotateCcw size={12} /> Start again
              </button>
            )}
          </div>
          {options && total > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {STEPS.map((s, i) => (
                <StepSelect
                  key={s.key}
                  index={i}
                  label={s.label}
                  testId={s.testId}
                  value={sel[s.key]}
                  onChange={(v) => choose(i, v)}
                  options={options[s.key] || []}
                  disabled={i > 0 && !sel[STEPS[i - 1].key]}
                />
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-linebright bg-white p-8 text-center" data-testid="td-empty">
              <Gauge size={28} className="mx-auto text-regal-amber" strokeWidth={1.5} />
              <p className="mt-3 font-display text-base font-bold uppercase tracking-wide text-ink">Tuning data is being added</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">The full vehicle gains database is being populated from verified calibration data. In the meantime, request a quote and we'll confirm figures for your exact vehicle.</p>
            </div>
          )}
        </div>

        {/* RESULT */}
        {busy && <div className="mt-8 h-40 animate-pulse border border-line bg-white" data-testid="td-loading" />}
        {!busy && result && (
          <Reveal className="mt-8">
            <section className="border border-line" data-testid="td-result">
              <div className="border-b border-line bg-white p-6 sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">{result.vehicle_type}</p>
                <h2 className="mt-2 font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-ink" data-testid="td-result-title">
                  {result.make} {result.model} <span className="text-regal-amber">{result.engine}</span>
                </h2>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="td-result-specs">
                  {[result.generation, result.engine_size, result.fuel, years, ecu ? `ECU: ${[ecu.maker, ecu.designation].filter(Boolean).join(" ")}` : null].filter(Boolean).map((chip) => (
                    <span key={chip} className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500">{chip}</span>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] bg-white text-left" data-testid="td-table">
                <thead>
                  <tr className="border-b border-line">
                    <th className="px-4 py-3 sm:px-6"><span className="sr-only">Measure</span></th>
                    <th className="px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 sm:px-6">Original</th>
                    <th className="bg-regal-amber px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-white sm:px-6">Stage 1</th>
                    <th className="px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-700 sm:px-6">Gain</th>
                    {extraStages.map((s) => (
                      <th key={s.key} className="bg-regal-amber px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-white sm:px-6" data-testid={`td-col-${s.key}`}>{s.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <PerfRow label="Power" unit="BHP" original={result.original_bhp} tuned={result.stage1_bhp} gain={result.gain_bhp} extraStages={extraStages} testId="td-row-power" s1Estimated={isEstimated} />
                  <PerfRow label="Torque" unit="Nm" original={result.original_nm} tuned={result.stage1_nm} gain={result.gain_nm} extraStages={extraStages} testId="td-row-torque" s1Estimated={isEstimated} originalEstimated={!!result.original_nm_estimated} />
                </tbody>
              </table>
              </div>

              {isEstimated && (
                <p className="border-t border-line bg-white px-6 py-4 text-xs leading-relaxed text-neutral-500" data-testid="td-estimate-note">
                  {ESTIMATE_NOTE}
                </p>
              )}

              {hasStage1 && (
                <div className="grid gap-8 border-t border-line bg-surface p-6 sm:grid-cols-2 sm:p-8" data-testid="td-bars">
                  {result.stage1_bhp != null && result.original_bhp != null && <CompareBar label="Power" unit="BHP" original={result.original_bhp} tuned={result.stage1_bhp} testId="td-bar-power" />}
                  {result.stage1_nm != null && result.original_nm != null && <CompareBar label="Torque" unit="Nm" original={result.original_nm} tuned={result.stage1_nm} testId="td-bar-torque" />}
                </div>
              )}

              {!hasStage1 && (
                <div className="border-t border-line bg-surface p-6 sm:p-8" data-testid="td-pending-note">
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-regal-amber"><Gauge size={14} /> Factory specification shown</p>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-400">
                    Stage 1 figures for this vehicle are being verified by our calibration team and are not published yet. Request a quote and we'll confirm the expected gains for your exact vehicle before any work begins.
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-line bg-white p-6 sm:flex-row sm:items-center sm:p-8">
                <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="td-cta-remap" className="inline-flex items-center justify-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors">
                  Get This Remap <ArrowUpRight size={15} />
                </a>
                <Link to={quoteUrl} data-testid="td-cta-quote" className="inline-flex items-center justify-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                  Request a Quote
                </Link>
                {result.page_url && (
                  <Link to={result.page_url} data-testid="td-cta-fullpage" className="inline-flex items-center justify-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                    Full Vehicle Page <ArrowUpRight size={15} />
                  </Link>
                )}
                <p className="text-xs text-neutral-500 sm:ml-auto sm:max-w-[220px]">Existing customers upload their ECU file via the File Portal. New to Regal? Request a quote first.</p>
              </div>
            </section>
          </Reveal>
        )}
        {!busy && miss && (
          <div className="mt-8 border border-line bg-surface p-8 text-center" data-testid="td-not-found">
            <p className="font-display text-base font-bold uppercase tracking-wide text-ink">We couldn't find that exact vehicle</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">Coverage is added continuously — <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">ask our team</Link> and we'll confirm support and expected gains.</p>
          </div>
        )}

        {/* SEO CONTENT */}
        <div className="mt-20 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="overline-label mb-4">How gains are measured</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Verified on the dyno, proven on the road</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-400">
              Every Stage 1 figure published here comes from calibration data developed and verified by Regal Tuning — measured on our in-house 4WD dynamometer and proven across more than 20 years of ECU calibration work and a worldwide dealer network. If a figure isn't verified, it isn't published.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              Original figures are the manufacturer's own published factory specifications for that exact engine variant, so the comparison you see is always against a genuine baseline.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline-label mb-4">Reading the results</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Original vs Stage 1 — what the numbers mean</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-400">
              <strong className="text-ink">Original</strong> is your vehicle's factory output. <strong className="text-regal-amber">Stage 1</strong> is the calibrated output after a software-only ECU remap — no hardware changes needed. <strong className="text-emerald-700">Gain</strong> is the exact difference your vehicle gains in power (BHP) and torque (Nm).
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              Stage 1 calibrations stay within safe tolerances for the engine, gearbox and drivetrain, and every file is supplied through the Regal Tuning file portal with full technical support.
            </p>
          </Reveal>
        </div>

        {/* FAQ */}
        <div className="mt-20" data-testid="td-faq">
          <p className="overline-label mb-4">Tuning Database FAQ</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Common questions</h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            {FAQS.map(([q, a], i) => (
              <Reveal key={q} delay={i * 0.05}>
                <div className="h-full border border-line bg-white p-6" data-testid={`td-faq-${i}`}>
                  <p className="font-display text-base font-bold uppercase tracking-wide text-ink">{q}</p>
                  <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">{a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* BOTTOM CTA */}
        <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-line bg-surface p-8 sm:flex-row sm:items-center" data-testid="td-bottom-cta">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber"><ShieldCheck size={14} /> 20+ years of ECU calibration</p>
            <h2 className="mt-2 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-ink">Can't find your vehicle? We can almost certainly still tune it.</h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-400">This database shows a fraction of our coverage and grows continuously. Tell us your vehicle and we'll confirm support and expected gains.</p>
          </div>
          <Link to="/contact" data-testid="td-bottom-cta-button" className="inline-flex shrink-0 items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
            Request a Quote <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </main>
  );
}
